/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { forwardRef } from "react";
import { cn } from "../../lib/utils";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "danger" | "social";
  size?: "sm" | "md" | "lg" | "xl";
  children: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = "primary", size = "md", children, ...props },
    ref,
  ) => {
    return (
      <button
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-200 ease-out",
          "focus:outline-none focus:ring-2 focus:ring-interactive/30",
          "disabled:opacity-50 disabled:pointer-events-none",
          "cursor-pointer",

          variant === "primary" && [
            "bg-interactive text-bg-base",
            "hover:bg-interactive-hover hover:shadow-elevated hover:-translate-y-0.5",
            "active:translate-y-0",
          ],
          variant === "secondary" && [
            "bg-bg-elevated border border-border text-text-primary",
            "hover:bg-bg-hover hover:border-border-hover hover:-translate-y-0.5",
            "hover:shadow-card",
            "active:translate-y-0",
          ],
          variant === "outline" && [
            "bg-bg-elevated border border-border text-text-secondary",
            "hover:text-interactive hover:border-border-hover hover:-translate-y-0.5",
            "hover:shadow-card",
            "active:translate-y-0",
          ],
          variant === "ghost" && [
            "text-text-tertiary border border-transparent hover:border-border hover:bg-bg-hover hover:text-interactive",
          ],
          variant === "danger" && [
            "text-error border border-transparent hover:border-error-border hover:bg-error-bg",
          ],
          variant === "social" && [
            "bg-bg-elevated border border-border text-text-primary",
            "hover:border-border-hover hover:-translate-y-0.5",
            "hover:shadow-card",
          ],

          // Heights align to the form-control scale: md matches Input/Select at h-11 exactly.
          {
            "h-9 px-3 text-sm": size === "sm",
            "h-11 px-5 text-[15px]": size === "md",
            "h-12 px-6 text-base": size === "lg",
            "h-14 px-8 text-lg": size === "xl",
          },

          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button };
